'use client';

import Icon from '@/components/ui/AppIcon';

interface PortSustainabilityProps {
  currentLanguage: string;
}

const PortSustainability = ({ currentLanguage }: PortSustainabilityProps) => {
  const content = {
    en: {
      title: 'Port & Maritime Sustainability',
      subtitle: 'Green logistics and emission reduction strategies',
      metrics: [
        { label: 'Emission Reduction', value: '45%', icon: 'CloudIcon' },
        { label: 'Fuel Efficiency', value: '35%', icon: 'BoltIcon' },
        { label: 'Green Operations', value: '80%', icon: 'SparklesIcon' }
      ],
      solutions: [
        {
          title: 'Ship Emission Monitoring',
          description: 'Real-time tracking of vessel emissions with AI-powered analytics',
          icon: 'SignalIcon',
          features: ['AIS integration', 'NOx/SOx tracking', 'Fuel consumption analysis']
        },
        {
          title: 'Port Operations Optimization',
          description: 'Smart scheduling and resource allocation for minimal environmental impact',
          icon: 'CogIcon',
          features: ['Berth optimization', 'Cargo handling efficiency', 'Energy management']
        },
        {
          title: 'Alternative Fuel Adoption',
          description: 'Transition to LNG, hydrogen, and shore power solutions',
          icon: 'BeakerIcon',
          features: ['Fuel compatibility assessment', 'Infrastructure planning', 'Cost-benefit analysis']
        }
      ]
    },
    hi: {
      title: 'बंदरगाह और समुद्री स्थिरता',
      subtitle: 'हरित लॉजिस्टिक्स और उत्सर्जन में कमी की रणनीतियां',
      metrics: [
        { label: 'उत्सर्जन में कमी', value: '45%', icon: 'CloudIcon' },
        { label: 'ईंधन दक्षता', value: '35%', icon: 'BoltIcon' },
        { label: 'हरित संचालन', value: '80%', icon: 'SparklesIcon' }
      ],
      solutions: [
        {
          title: 'जहाज उत्सर्जन निगरानी',
          description: 'AI-संचालित विश्लेषण के साथ पोत उत्सर्जन की वास्तविक समय ट्रैकिंग',
          icon: 'SignalIcon',
          features: ['AIS एकीकरण', 'NOx/SOx ट्रैकिंग', 'ईंधन खपत विश्लेषण']
        },
        {
          title: 'बंदरगाह संचालन अनुकूलन',
          description: 'न्यूनतम पर्यावरणीय प्रभाव के लिए स्मार्ट शेड्यूलिंग और संसाधन आवंटन',
          icon: 'CogIcon',
          features: ['बर्थ अनुकूलन', 'कार्गो हैंडलिंग दक्षता', 'ऊर्जा प्रबंधन']
        },
        {
          title: 'वैकल्पिक ईंधन अपनाना',
          description: 'LNG, हाइड्रोजन और शोर पावर समाधान में संक्रमण',
          icon: 'BeakerIcon',
          features: ['ईंधन संगतता मूल्यांकन', 'बुनियादी ढांचा योजना', 'लागत-लाभ विश्लेषण']
        }
      ]
    },
    ar: {
      title: 'استدامة الموانئ والبحرية',
      subtitle: 'استراتيجيات الخدمات اللوجستية الخضراء وخفض الانبعاثات',
      metrics: [
        { label: 'خفض الانبعاثات', value: '45%', icon: 'CloudIcon' },
        { label: 'كفاءة الوقود', value: '35%', icon: 'BoltIcon' },
        { label: 'العمليات الخضراء', value: '80%', icon: 'SparklesIcon' }
      ],
      solutions: [
        {
          title: 'مراقبة انبعاثات السفن',
          description: 'تتبع في الوقت الفعلي لانبعاثات السفن بتحليلات مدعومة بالذكاء الاصطناعي',
          icon: 'SignalIcon',
          features: ['تكامل AIS', 'تتبع NOx/SOx', 'تحليل استهلاك الوقود']
        },
        {
          title: 'تحسين عمليات الموانئ',
          description: 'الجدولة الذكية وتخصيص الموارد لتقليل التأثير البيئي',
          icon: 'CogIcon',
          features: ['تحسين الرصيف', 'كفاءة مناولة البضائع', 'إدارة الطاقة']
        },
        {
          title: 'اعتماد الوقود البديل',
          description: 'الانتقال إلى حلول LNG والهيدروجين وطاقة الشاطئ',
          icon: 'BeakerIcon',
          features: ['تقييم توافق الوقود', 'تخطيط البنية التحتية', 'تحليل التكلفة والفائدة']
        }
      ]
    }
  };

  const currentContent = content[currentLanguage as keyof typeof content] || content.en;

  return (
    <section className="py-16 md:py-24 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center space-y-4 mb-12">
          <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary">
            {currentContent.title}
          </h2>
          <p className="text-lg text-text-secondary max-w-2xl mx-auto">
            {currentContent.subtitle}
          </p>
        </div>

        {/* Metrics */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          {currentContent.metrics.map((metric, index) => (
            <div 
              key={index}
              className="bg-gradient-to-br from-primary to-primary/80 rounded-xl p-8 text-center shadow-natural"
            >
              <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name={metric.icon as any} size={32} className="text-white" />
              </div>
              <div className="text-5xl font-bold text-white mb-2">{metric.value}</div>
              <div className="text-white/90 font-medium">{metric.label}</div>
            </div>
          ))}
        </div>

        {/* Solutions */}
        <div className="space-y-6">
          {currentContent.solutions.map((solution, index) => (
            <div 
              key={index}
              className="bg-card border border-border rounded-xl p-6 hover:border-primary/50 hover:shadow-natural transition-all duration-300"
            >
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name={solution.icon as any} size={24} className="text-primary" />
                </div>
                <div className="flex-1">
                  <h3 className="font-heading font-bold text-xl text-text-primary mb-2">
                    {solution.title}
                  </h3>
                  <p className="text-text-secondary mb-4">
                    {solution.description}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {solution.features.map((feature, fIndex) => (
                      <span 
                        key={fIndex}
                        className="px-3 py-1 bg-primary/10 text-primary text-sm rounded-full border border-primary/20"
                      >
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PortSustainability;